import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { core, ExchangeRateService, ConfigService } from '@icc/shared';
import { CalculateDiscountsAndMultipliersService } from './calculate-discounts-and-multipliers.service';
import { Common, ToastService } from '@icc/shared/helpers';
import { Discount } from '../../../../../shared/types/src/lib/price/discount.interface';
import { PriceModifier } from '../interfaces/price-modifier.interface';
import { TaxRate } from '../interfaces/tax-rate.interface';
import { SalesDocumentPosition } from '../models/sales-document-position.model';
import { SalesDocument } from '../models/sales-document.model';
import { ReplaySubject } from 'rxjs';
import { NgxPermissionsService } from 'ngx-permissions';
import { TranslateService } from '@ngx-translate/core';
import { Promotion } from '../interfaces/promotion.interface';

@Injectable({
    providedIn: 'root',
})
export class DiscountsAndMultipliersService {
    buyDiscounts: PriceModifier[] = [];
    salesDiscounts: PriceModifier[] = [];
    salesMultipliers: PriceModifier[] = [];
    promotions: Promotion[] = [];
    productPromotions: Record<number, Promotion> = {};
    configuratorPromotions: Record<number, Promotion> = {};
    charge = 1;
    marketFactor = 1;
    offerDiscounts = {
        defaultDiscount: 0,
        maxDiscount: null,
    };

    buyDiscountsRevision = 0;
    salesDiscountsRevision = 0;
    salesMultipliersRevision = 0;
    promotionsRevision = 0;
    chargeRevision = 0;
    offerDiscountsRevision = 0;
    marketFactorRevision = 0;

    loaded$ = new ReplaySubject<boolean>();
    loadedMultipliers$ = new ReplaySubject<boolean>();
    loaded = false;
    loadedMultipliers = false;

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private exchangeRateService: ExchangeRateService,
        private permissionsService: NgxPermissionsService,
        private toastService: ToastService,
        private translateService: TranslateService
    ) {}

    public async loadDiscountsAndMultipliers(contractorId = null) {
        const {
            charge,
            chargeRevision,
            marketFactor,
            marketFactorRevision,
            buyDiscounts,
            buyDiscountsRevision,
            salesDiscounts,
            salesDiscountRevision,
            salesMultipliers,
            salesMultipliersRevision,
            offerDiscounts,
            promotions,
            promotionsRevision,
        } = await this.http
            .get<{
                charge: number;
                chargeRevision: number;
                marketFactor: number;
                marketFactorRevision: number;
                buyDiscounts: PriceModifier[];
                buyDiscountsRevision: number;
                salesDiscounts: PriceModifier[];
                salesDiscountRevision: number;
                salesMultipliers: PriceModifier[];
                salesMultipliersRevision: number;
                promotions: Promotion[];
                promotionsRevision: number;
                offerDiscounts: {
                    defaultDiscount: number;
                    maxDiscount: number | null;
                    revision: number;
                };
            }>(
                `${window.location.origin}/api/b2b/price-modifiers${
                    contractorId ? '/' + contractorId : ''
                }`
            )
            .toPromise();
        this.charge = charge;
        this.marketFactor = marketFactor;
        this.offerDiscounts = offerDiscounts;

        this.chargeRevision = chargeRevision;
        this.marketFactorRevision = marketFactorRevision;
        this.offerDiscountsRevision = offerDiscounts.revision;

        this.loaded$.next(true);
        this.loaded$.complete();
        this.loaded = true;

        this.buyDiscounts = buyDiscounts.map((el) => {
            const dateFrom = el.dateFrom ? new Date(el.dateFrom) : el.dateFrom;
            const dateTo = el.dateTo ? new Date(el.dateTo) : el.dateTo;
            if (dateFrom) {
                dateFrom.setHours(0, 0, 0, 0);
            }
            if (dateTo) {
                dateTo.setHours(0, 0, 0, 0);
            }
            return {
                ...el,
                dateFrom,
                dateTo,
                documentPositionType: core.parseJson(el.documentPositionType),
            };
        });
        this.buyDiscountsRevision = buyDiscountsRevision;

        this.salesDiscounts = salesDiscounts.map((el) => {
            const dateFrom = el.dateFrom ? new Date(el.dateFrom) : el.dateFrom;
            const dateTo = el.dateTo ? new Date(el.dateTo) : el.dateTo;
            if (dateFrom) {
                dateFrom.setHours(0, 0, 0, 0);
            }
            if (dateTo) {
                dateTo.setHours(0, 0, 0, 0);
            }
            return {
                ...el,
                dateFrom,
                dateTo,
                documentPositionType: core.parseJson(el.documentPositionType),
            };
        });
        this.salesDiscountsRevision = salesDiscountRevision;

        this.salesMultipliers = salesMultipliers.map((el) => {
            const dateFrom = el.dateFrom ? new Date(el.dateFrom) : el.dateFrom;
            const dateTo = el.dateTo ? new Date(el.dateTo) : el.dateTo;
            if (dateFrom) {
                dateFrom.setHours(0, 0, 0, 0);
            }
            if (dateTo) {
                dateTo.setHours(0, 0, 0, 0);
            }
            return {
                ...el,
                dateFrom,
                dateTo,
                documentPositionType: core.parseJson(el.documentPositionType),
            };
        });
        this.salesMultipliersRevision = salesMultipliersRevision;

        this.promotions = promotions.map((el) => {
            const dateFrom = el.dateFrom ? new Date(el.dateFrom) : el.dateFrom;
            const dateTo = el.dateTo ? new Date(el.dateTo) : el.dateTo;
            if (dateFrom) {
                dateFrom.setHours(0, 0, 0, 0);
            }
            if (dateTo) {
                dateTo.setHours(0, 0, 0, 0);
            }
            return {
                ...el,
                dateFrom,
                dateTo,
                record: core.parseJson(el.record),
            };
        });
        this.promotionsRevision = promotionsRevision;

        this.updateProductPromotions();

        this.loadedMultipliers$.next(true);
        this.loadedMultipliers$.complete();
        this.loadedMultipliers = true;
    }

    // eslint-disable-next-line max-statements
    public async calculateDiscountsAndMultipliers(
        document: SalesDocument,
        position: SalesDocumentPosition,
        type: 'buy' | 'sales',
        taxRate?: TaxRate
    ) {
        const canExceedMaxDiscount = await this.permissionsService.hasPermission([
            'not-applicable-max-discount',
        ]);
        const { maxDiscountExceeded, updatedPositionConfiguration } =
            CalculateDiscountsAndMultipliersService.calculateDiscountsAndMultipliers({
                document,
                position,
                type,
                discounts: type === 'buy' ? this.buyDiscounts : this.salesDiscounts,
                promotions: this.promotions,
                exchangeRates:
                    type === 'buy'
                        ? this.exchangeRateService.buyExchangeRates
                        : this.exchangeRateService.exchangeRates,
                taxRate,
                canExceedMaxDiscount,
                revisions: {
                    discounts:
                        type === 'buy' ? ++this.buyDiscountsRevision : ++this.salesDiscountsRevision,
                    promotions: ++this.promotionsRevision,
                }
            });

        if (maxDiscountExceeded) {
            if (canExceedMaxDiscount) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat pozycji'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat pozycji'
                    ),
                    severity: 'warn',
                });
            } else {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat pozycji'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat pozycji. Ustawiono maksymalną wartość'
                    ),
                    severity: 'warn',
                });
            }
        }

        return { updatedPositionConfiguration };
    }

    getPositionMultiplier(position: SalesDocumentPosition) {
        let multiplier: number | null = null;
        if (!Common.isObject(position.configuration)) {
            position.configuration = core.parseJson(position.configuration);
        }
        if (position.type === 'complex') {
            multiplier = this.getMultiplier(
                'Configurator.ComplexConfigurators',
                position.configuration.configuratorId
            );
        } else if (position.type === 'static') {
            multiplier = this.getMultiplier('Product.Products', position.configuration.productId);
        }
        return multiplier || 1;
    }

    getProductMultiplier(productId: number) {
        const multiplier = this.getMultiplier('Product.Products', productId);
        return multiplier || 1;
    }

    getMultiplier(model: string, foreignKey: string | number, model2?: string, foreignKey2?: string | number): number {
        const multiplier = this.salesMultipliers.find(
            (el) => {
                const matchesModel1 = el.model === model && el.foreignKey === Number(foreignKey);
                // If model2/foreignKey2 search params are provided, check them
                if (model2 !== undefined && foreignKey2 !== undefined) {
                    return (
                        matchesModel1 &&
                        el.model2 === model2 &&
                        el.foreignKey2 === Number(foreignKey2)
                    ) || (
                        el.model === model2 &&
                        el.foreignKey === Number(foreignKey2) &&
                        el.model2 === model &&
                        el.foreignKey2 === Number(foreignKey)
                    );
                }
                if (!matchesModel1) {
                    return false;
                }
                // If no model2 search params, match entries without model2 or with any model2
                return true;
            }
        );

        return multiplier?.value || 1;
    }

    async changeDiscount(
        position: SalesDocumentPosition,
        document: SalesDocument,
        discount: Discount,
        type: 'percent' | 'value',
        value: number
    ) {
        const canExceedMaxDiscount = await this.permissionsService.hasPermission([
            'not-applicable-max-discount',
        ]);
        const positionDiscount = position.priceDiscounts.find((el) => el.type === discount.type);
        positionDiscount.discountType = type;
        positionDiscount.edited = true;

        let updatedConfiguration = false;

        if (
            position.configuration?.priceParts?.length && position.type !== 'complex' &&
            (type === 'value' || positionDiscount.discountType === 'value')
        ) {
            type = 'percent';
            positionDiscount.discount = core.round((value / positionDiscount.price) * 100);
            value = positionDiscount.discount;
            positionDiscount.discountType = type;
            positionDiscount.maxDiscountType = type;
        }

        let priceAfterDiscountsValue = 0;
        if (
            position.configuration?.priceParts?.length &&
            positionDiscount.baseUnitPrices?.length &&
            type === 'percent'
        ) {
            positionDiscount.value = 0;
            positionDiscount.discount = value;
            positionDiscount.baseValue = 0;
            positionDiscount.baseUnitPrices.forEach((el) => {
                let newValue = 0;
                let baseValue = 0;
                newValue = core.round((core.round(el.unitPrice) * value) / 100);
                baseValue = this.exchangeRateService.exchange(
                    newValue,
                    document.priceCurrency || 'PLN',
                    position.basePriceCurrency
                );
                const pricePart = position.configuration.priceParts.find(
                    (part) =>
                        part.valueId === el.pricePartValueId &&
                        part.percentId === el.pricePartPercentId
                );
                pricePart.priceBeforeDiscounts = el.unitPrice;
                pricePart.discount = {
                    name: pricePart.discount?.name || positionDiscount?.name,
                    discount: value,
                    baseValue,
                    value: newValue,
                    type: positionDiscount?.type ?? 'main',
                    discountType: type ?? 'percent',
                    edited: positionDiscount?.edited ?? false,
                    external: positionDiscount?.external ?? false,
                };
                if (pricePart.priceBeforeDiscounts !== null) {
                    pricePart.priceAfterDiscounts = core.round(
                        core.round(pricePart.priceBeforeDiscounts - newValue) * pricePart.quantity
                    );
                }
                pricePart.priceBeforeDiscounts = core.round(
                    pricePart.priceBeforeDiscounts * pricePart.quantity
                );

                positionDiscount.value += core.round(newValue * el.quantity);
                positionDiscount.baseValue += core.round(baseValue * el.quantity);
            });
            if (position.type !== 'complex') {
                position.configuration.priceParts.forEach((part) => {
                    priceAfterDiscountsValue += part.priceAfterDiscounts;
                });
            }
            updatedConfiguration = true;
        } else {
            if (type === 'percent') {
                positionDiscount.discount = value;
                positionDiscount.value = core.round((positionDiscount.price * value) / 100);
                positionDiscount.baseValue = this.exchangeRateService.exchange(
                    positionDiscount.value,
                    position.priceCurrency,
                    position.basePriceCurrency
                );
            } else {
                positionDiscount.value = value;
                positionDiscount.baseValue = this.exchangeRateService.exchange(
                    value,
                    position.priceCurrency,
                    position.basePriceCurrency
                );
                positionDiscount.discount = core.round((value / positionDiscount.price) * 100);
            }
        }

        if (
            positionDiscount.maxDiscount !== null &&
            ((positionDiscount.maxDiscountType === 'percent' &&
                Number(positionDiscount.discount) > Number(positionDiscount.maxDiscount)) ||
                (positionDiscount.maxDiscountType === 'value' &&
                    Number(positionDiscount.value) > Number(positionDiscount.maxDiscount)))
        ) {
            const currency = this.exchangeRateService.getCurrency(position.priceCurrency);
            if (canExceedMaxDiscount) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat pozycji'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Maksymalny rabat pozycji wynosi {maxDiscount}. Przekroczono rabat o {exceededDiscount}',
                        {
                            maxDiscount: CalculateDiscountsAndMultipliersService.formatDiscount(
                                positionDiscount.maxDiscount,
                                positionDiscount.maxDiscountType,
                                this.translateService.currentLang,
                                currency
                            ),
                            exceededDiscount:
                                CalculateDiscountsAndMultipliersService.formatDiscount(
                                    (positionDiscount.maxDiscountType === 'percent'
                                        ? Number(positionDiscount.discount)
                                        : Number(positionDiscount.value)) -
                                        positionDiscount.maxDiscount,
                                    positionDiscount.maxDiscountType,
                                    this.translateService.currentLang,
                                    currency
                                ),
                        }
                    ),
                    severity: 'warn',
                });
            } else {
                positionDiscount.discountType = positionDiscount.maxDiscountType;
                if (positionDiscount.maxDiscountType === 'percent') {
                    positionDiscount.discount = positionDiscount.maxDiscount;
                    positionDiscount.value = core.round(
                        (positionDiscount.price * positionDiscount.maxDiscount) / 100
                    );
                    positionDiscount.baseValue = this.exchangeRateService.exchange(
                        positionDiscount.value,
                        position.priceCurrency,
                        position.basePriceCurrency
                    );
                } else {
                    positionDiscount.value = positionDiscount.maxDiscount;
                    positionDiscount.baseValue = this.exchangeRateService.exchange(
                        positionDiscount.maxDiscount,
                        position.priceCurrency,
                        position.basePriceCurrency
                    );
                    positionDiscount.discount = core.round(
                        (positionDiscount.maxDiscount / positionDiscount.price) * 100
                    );
                }
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Przekroczono maksymalny rabat pozycji'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Maksymalny rabat pozycji wynosi {maxDiscount}. Ustawiono maksymalną wartość',
                        {
                            maxDiscount: CalculateDiscountsAndMultipliersService.formatDiscount(
                                positionDiscount.maxDiscount,
                                positionDiscount.maxDiscountType,
                                this.translateService.currentLang,
                                currency
                            ),
                        }
                    ),
                    severity: 'warn',
                });
            }
        }
        if (position.priceBeforeDiscountsValue !== null) {
            if (priceAfterDiscountsValue != 0) {
                position.priceAfterDiscountsValue = priceAfterDiscountsValue;
            } else {
                position.priceAfterDiscountsValue = position.priceDiscounts.reduce(
                    (prev, curr) => prev - curr.value,
                    position.priceBeforeDiscountsValue *
                        (document.discountType === 'price' ? 1 : position.quantity)
                );
            }
        } else {
            position.priceAfterDiscountsValue = null;
        }

        return { updatedConfiguration };
    }

    recalculateDiscounts(position: SalesDocumentPosition, salesDocument: SalesDocument) {
        position.priceDiscounts.forEach((discount) => {
            discount.price = position.priceBeforeDiscountsValue;
            if (discount.discountType === 'percent') {
                discount.value = core.round(
                    (position.priceBeforeDiscountsValue * discount.discount) / 100
                );
            } else {
                discount.discount = core.round(
                    (discount.value / position.priceBeforeDiscountsValue) * 100
                );
            }
        });
        if (position.priceBeforeDiscountsValue !== null) {
            position.priceAfterDiscountsValue = position.priceDiscounts.reduce(
                (prev, curr) => prev - curr.value,
                position.priceBeforeDiscountsValue *
                    (salesDocument.discountType === 'price' ? 1 : position.quantity)
            );
        } else {
            position.priceAfterDiscountsValue = null;
        }
    }

    getProductSalesPrice(value: number, productId: number) {
        if (value === null) {
            return null;
        }
        const multiplier = this.getMultiplier('Product.Products', productId) || 1;
        return value * multiplier * this.charge * this.marketFactor;
    }

    getConfiguratorSalesPrice(value: number, configuratorId: number) {
        if (value === null) {
            return null;
        }
        const multiplier =
            this.getMultiplier('Configurator.ComplexConfigurators', configuratorId) || 1;
        return value * multiplier * this.charge * this.marketFactor;
    }

    getPositionSalesPrice(value: number, position: SalesDocumentPosition) {
        if (value === null) {
            return null;
        }
        const multiplier = this.getPositionMultiplier(position) || 1;
        return value * multiplier * this.charge * this.marketFactor;
    }

    updateProductPromotions() {
        this.promotions.forEach((promotion) => {
            if (promotion.model === 'Products') {
                promotion.record.forEach((productId) => {
                    if (
                        this.productPromotions[productId] &&
                        this.productPromotions[productId].priority > promotion.priority
                    ) {
                        return;
                    }
                    this.productPromotions[productId] = promotion;
                });
                if (!promotion.record.length) {
                    this.productPromotions[-1] = promotion;
                }
            } else if (promotion.model === 'ComplexConfigurators') {
                promotion.record.forEach((configuratorId) => {
                    if (
                        this.configuratorPromotions[configuratorId] &&
                        this.configuratorPromotions[configuratorId].priority > promotion.priority
                    ) {
                        return;
                    }
                    this.configuratorPromotions[configuratorId] = promotion;
                });
                if (!promotion.record.length) {
                    this.configuratorPromotions[-1] = promotion;
                }
            }
        });
    }

    getProductPromotion(productId: number) {
        if (this.productPromotions[-1]?.priority > this.productPromotions[productId]?.priority) {
            return this.productPromotions[-1];
        }
        return this.productPromotions[productId] ?? this.productPromotions[-1];
    }

    getConfiguratorPromotion(configuratorId: number) {
        if (
            this.configuratorPromotions[-1]?.priority >
            this.configuratorPromotions[configuratorId]?.priority
        ) {
            return this.configuratorPromotions[-1];
        }
        return this.configuratorPromotions[configuratorId] ?? this.configuratorPromotions[-1];
    }

    getPositionPromotion(position: SalesDocumentPosition) {
        return CalculateDiscountsAndMultipliersService.getPositionPromotion(
            position,
            this.promotions
        );
    }
}
